import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ajvResolver,
  ButtonSecondary,
  ResponseError,
  Popup,
  responseErrorsResolver,
  ApplicationSelector,
  SelectListPosition,
  IconLoader,
  PermissionEnum,
  usePrevious,
  formatDate,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  ApplicationSchemeType,
  CorporateQuestionnarieFormValues,
} from '../../../hoc/customers/types';
import { defaultFormValues as cofDefaultFormValues } from '../../../hoc/individual/store/slices/create';
import { updateMainRepresentativeValues } from '../../../hoc/new-application/corporate/store/slices';
import {
  NewApplicationListItem,
  NewApplicationChildrenInfo,
  CompanyOfficialFormValues,
  ApplicationType,
  NewApplicationStatus,
  AccessRight,
} from '../../../hoc/new-application/types';
import {
  useCreateCompanyOfficialMutation,
  useAssignCompanyOfficialMutation,
  useUpdateCorporateQuestionnarieMutation,
} from '../../../hoc/new-application/store/services';
import getCorporateAppicationBody from '../../../hoc/new-application/corporate/helpers/get-corporate-application-body';
import CompanyOfficial from './company-official';
import CreateForm from './create-company-official';
import useGetAutocompleteList from '../../../hoc/new-application/helpers/use-autocomplete-list';
import getCorporateQuestionnarieBody from '../../../hoc/new-application/corporate/helpers/get-questionnarie-body';
import { CorporateApplicationStepFiveTypes } from '../types';
import StepPartBlock from './parts/step-part-block';

import cn from '../new-application.module.css';

const schema = {
  type: 'object',
  properties: {
    firstName: {
      type: 'string',
      minLength: 1,
      errorMessage: 'First Name is required',
    },
    lastName: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Last Name is required',
    },
    phone: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Phone is required',
    },
    email: {
      type: 'string',
      minLength: 1,
      errorMessage: 'E-mail is required',
    },
    dob: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Date of Birth is required',
    },
    signatureType: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Signature type is required',
    },
    accessRight: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Access Right type is required',
    },
    nationalityId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Nationality is required',
    },
  },
  required: [
    'firstName',
    'lastName',
    'phone',
    'dob',
    'signatureType',
    'accessRight',
    'email',
    'nationalityId',
  ],
};

const NewApplicationCorporateInfoStepFive = ({
  setValue,
  companyOfficial,
  applicationId,
  status,
  isEditable,
  applicationStatus,
  initialFields,
  fields,
  onSubmit: onApplicationSubmit,
  mainRepresentativeApplications,
  setDefaultFormValues,
  defaultFormValues: defaultApplicationFormValues,
  defaultQuestionnarieFormValues,
  applicationScheme,
  questionnarieFields,
  onSaveAccountsCurrencies,
  onSaveAccountsFlows,
  onSaveExpectedFundsOrigins,
}: CorporateApplicationStepFiveTypes) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [clearState, setClearState] = useState(false);
  const [sumSubError, setSumSubError] = useState('');
  const [sidepanelError, setSidepanelError] = useState('');

  const dispatch = useAppDispatch();

  const [companyOfficialList, setCompanyOfficialList] = useState<
    {
      id: string;
      position: string;
      percentage: string;
      natureOfControl: string;
      signatureType: string;
      status?: NewApplicationStatus;
      accessRight: AccessRight;
    }[]
  >([]);

  const {
    customersDictionaries: { activePermissions, applicationSchemes },
  } = useAppSelector((state) => state);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    setError,
    reset,
  } = useForm<CompanyOfficialFormValues>({
    defaultValues: cofDefaultFormValues,
    resolver: ajvResolver(schema),
  });

  const [updateCorporateQuestionnarie] =
    useUpdateCorporateQuestionnarieMutation();

  const prevCompanyOfficial = usePrevious(companyOfficial);

  const {
    autocompleteList,
    onSearch,
    isLoading: autocompleteIsLoading,
    getAutocompleteListAction,
    searchQuery,
    setSearchQuery,
  } = useGetAutocompleteList();

  const [create, { isLoading, isSuccess }] = useCreateCompanyOfficialMutation();
  const [assign, { isLoading: assignInProgress }] =
    useAssignCompanyOfficialMutation();
  const [addErrorVisible, setAddErrorVisible] = useState(false);

  const assignCompanyOfficial = async (id: string) => {
    const body = {
      companyOfficialApplicationId: id,
      companyOfficialType: ApplicationType.COMPANY_OFFICIAL,
    };

    const response = (await assign({
      id: applicationId as string,
      body,
    })) as {
      data?: { data: NewApplicationChildrenInfo };
      error?: ResponseError;
    };

    const nextFields = { ...fields };

    delete nextFields?.mainRepresentative;
    delete nextFields?.companyOfficial;

    if (!isEqual(nextFields, initialFields)) {
      const applictionBody = getCorporateAppicationBody(nextFields);

      if (!applictionBody.websiteLink) {
        applictionBody.websiteLink = '';
      }

      onApplicationSubmit({
        body: applictionBody,
        id: applicationId as string,
      });
    }

    if (!isEqual(questionnarieFields, defaultQuestionnarieFormValues)) {
      if (
        applicationScheme &&
        (applicationScheme?.accountantDetailsActive ||
          applicationScheme?.auditedByExternalComplianceActive ||
          applicationScheme?.changesInManagementOrOwnershipActive ||
          applicationScheme?.complianceConsultantDetailsActive ||
          applicationScheme?.fcaNumberActive ||
          applicationScheme?.hmrcMlrNumberActive ||
          applicationScheme?.hmrcRegistrationExpiryDateActive ||
          applicationScheme?.icoNumberActive ||
          applicationScheme?.itProviderDetailsActive ||
          applicationScheme?.mlroDetailsActive ||
          applicationScheme?.numberOfAgentsActive ||
          applicationScheme?.numberOfBranchesActive ||
          applicationScheme?.onboardCorporateClientsActive ||
          applicationScheme?.onboardMsbActive ||
          applicationScheme?.paymentsForCharitiesActive ||
          applicationScheme?.payoutPartnerDetailsActive ||
          applicationScheme?.regulatoryInvestigationActive ||
          applicationScheme?.screeningActive ||
          applicationScheme?.transactionMonitoringActive ||
          applicationScheme?.typeOfBusinessLicenceActive ||
          applicationScheme?.visitedByAuthoritiesActive ||
          applicationScheme?.workingWithAnyOtherIPSPActive ||
          applicationScheme?.id === 'cdbb8c71-90e6-4a5e-964f-e55169e0c4df')
      ) {
        const questionnarieBody = getCorporateQuestionnarieBody({
          questionnarieValues:
            questionnarieFields as CorporateQuestionnarieFormValues,
          scheme: applicationScheme,
        });

        updateCorporateQuestionnarie({
          id: applicationId as string,
          body: questionnarieBody,
        });

        if (applicationScheme?.id === 'cdbb8c71-90e6-4a5e-964f-e55169e0c4df') {
          onSaveAccountsCurrencies();
          onSaveAccountsFlows();
          onSaveExpectedFundsOrigins();
        }
      }
    }

    if (
      response.data &&
      response.data.data &&
      mainRepresentativeApplications?.length
    ) {
      if (
        mainRepresentativeApplications.find(
          (item) => item.id === response?.data?.data.id,
        )
      ) {
        const filteredMainRepresentative =
          mainRepresentativeApplications.filter(
            (item) => item.id !== response?.data?.data.id,
          );

        setValue('mainRepresentative', filteredMainRepresentative);

        setDefaultFormValues({
          ...defaultApplicationFormValues,
          mainRepresentative: filteredMainRepresentative,
        });

        dispatch(updateMainRepresentativeValues(filteredMainRepresentative));
      }
    }

    if (response.data || response.error) {
      setClearState(true);

      setTimeout(() => {
        setClearState(false);
        reset({
          accessRight: AccessRight.VIEWER,
          applicantId: '',
          deliveryChannelId: '',
          dob: '',
          email: '',
          firstName: '',
          isUSResident: false,
          lastName: '',
          nationalityId: '',
          participatesInPolitics: false,
          phone: '',
          signatureType: '',
          applicationSchemeId: '',
        });
        setSumSubError('');
        setSidepanelError('');
      }, 100);
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    setSumSubError('');
    setSidepanelError('');

    const nextFields = { ...fields };

    delete nextFields?.mainRepresentative;
    delete nextFields?.companyOfficial;

    if (!isEqual(nextFields, initialFields)) {
      const applictionBody = getCorporateAppicationBody(nextFields);

      if (!applictionBody.websiteLink) {
        applictionBody.websiteLink = '';
      }

      onApplicationSubmit({
        body: applictionBody,
        id: applicationId as string,
      });
    }

    const dob = new Date(values.dob as string);

    const body: CompanyOfficialFormValues = {
      firstName: values.firstName,
      lastName: values.lastName,
      phone: values.phone,
      email: values?.email,
      dob: `${dob.getFullYear()}-${(dob.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${dob.getDate().toString().padStart(2, '0')}`,
      nationalityId: values.nationalityId,
      deliveryChannelId: values.deliveryChannelId,
      isUSResident: values.isUSResident,
      participatesInPolitics: values.participatesInPolitics,
    };

    if (values.applicantId) {
      body.applicantId = values.applicantId;
    }

    if (values?.applicationSchemeId) {
      body.applicationSchemeId = values?.applicationSchemeId;
    }

    const response = (await create({
      id: applicationId as string,
      body,
    })) as {
      data?: { data: NewApplicationListItem };
      error?: ResponseError;
    };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError: setSidepanelError,
      });

      if (response.error.data && response.error.data.message) {
        if (
          Array.isArray(response.error.data.message) &&
          response.error.data.message[0] &&
          typeof response.error.data.message[0] === 'string' &&
          (response.error.data.message[0] as string).includes('applicant')
        ) {
          setSumSubError(response.error.data.message[0]);
        }
      }
    } else {
      reset({
        accessRight: AccessRight.VIEWER,
        applicantId: '',
        deliveryChannelId: '',
        dob: '',
        email: '',
        firstName: '',
        isUSResident: false,
        lastName: '',
        nationalityId: '',
        participatesInPolitics: false,
        phone: '',
        signatureType: '',
        applicationSchemeId: '',
      });
      const assignBody = {
        companyOfficialApplicationId: response?.data?.data?.id as string,
        companyOfficialType: ApplicationType.COMPANY_OFFICIAL,
        signatureType: values.signatureType,
        accessRight: values.accessRight || AccessRight.VIEWER,
      };

      const assignResponse = (await assign({
        id: applicationId as string,
        body: assignBody,
      })) as {
        data: { data?: NewApplicationChildrenInfo };
        error?: ResponseError;
      };

      if (!assignResponse.error && panelIsOpen) {
        setPanelIsOpen(false);
        setSidepanelError('');
        setSumSubError('');
      }
    }
  });

  useEffect(() => {
    if (companyOfficial?.length) {
      setCompanyOfficialList(
        companyOfficial.map((item) => ({
          id: item.subApplicationId,
          position: item.position,
          natureOfControl: item.natureOfControl,
          percentage: item.percentage,
          status: item.subApplication.status,
          signatureType: item.signatureType || '',
          accessRight: item.accessRight || AccessRight.NONE,
        })),
      );
    }
  }, [companyOfficial]);

  useEffect(() => {
    if (!isEqual(companyOfficial, prevCompanyOfficial)) {
      setCompanyOfficialList(
        companyOfficial?.map((item) => ({
          id: item.subApplicationId,
          position: item.position,
          natureOfControl: item.natureOfControl,
          percentage: item.percentage,
          status: item.subApplication.status,
          signatureType: item.signatureType || '',
          accessRight: item.accessRight || AccessRight.NONE,
        })) || [],
      );
    }
  }, [companyOfficial, prevCompanyOfficial]);

  useEffect(() => {
    if (isSuccess) {
      setPanelIsOpen(false);
      reset({
        accessRight: AccessRight.VIEWER,
        applicantId: '',
        deliveryChannelId: '',
        dob: '',
        email: '',
        firstName: '',
        isUSResident: false,
        lastName: '',
        nationalityId: '',
        participatesInPolitics: false,
        phone: '',
        signatureType: '',
        applicationSchemeId: '',
      });
    }
  }, [isSuccess]);

  return (
    <div className={cn.stepBlockWrapper} style={{ zIndex: 17 }}>
      <div className={cn.stepBlockContent}>
        <StepPartBlock title="Company official">
          <div className={cn.accountsCurrenciesList}>
            {companyOfficialList.map((item, index) => (
              <CompanyOfficial
                key={item.id}
                value={item}
                setValue={setValue}
                setValuesList={setCompanyOfficialList}
                index={index}
                values={companyOfficialList}
                applicationId={applicationId}
                applicationStatus={applicationStatus}
                companyOfficial={companyOfficial?.find(
                  (user) => item.id === user.subApplicationId,
                )}
                initialFields={initialFields}
                fields={fields}
                onApplicationSubmit={onApplicationSubmit}
                disabled={
                  !isEditable ||
                  Boolean(
                    status === NewApplicationStatus.ACCEPTED ||
                    status === NewApplicationStatus.DECLINED,
                  )
                }
                defaultQuestionnarieFormValues={defaultQuestionnarieFormValues}
                applicationScheme={applicationScheme}
                questionnarieFields={questionnarieFields}
                auditLogAvailable={activePermissions?.includes(
                  PermissionEnum.AUDIT_LOG_LIST,
                )}
                type="COF"
                onSaveAccountsCurrencies={onSaveAccountsCurrencies}
                onSaveAccountsFlows={onSaveAccountsFlows}
                onSaveExpectedFundsOrigins={onSaveExpectedFundsOrigins}
              />
            ))}
            {addErrorVisible && (
              <div className={cn.sameUserError}>You can`t add same user.</div>
            )}
            {applicationStatus !== NewApplicationStatus.ACCEPTED &&
              applicationStatus !== NewApplicationStatus.DECLINED &&
              (activePermissions?.includes(
                PermissionEnum.INDIVIDUAL_CUSTOMERS_EDIT_DETAILS_ACTION,
              ) ||
                activePermissions?.includes(
                  PermissionEnum.CORPORATE_CUSTOMERS_EDIT_DETAILS_ACTION,
                )) && (
                <div className={cn.addNewCofWrapper}>
                  {!assignInProgress && (
                    <ApplicationSelector
                      title="Add Company officials"
                      placeholder="Select customer..."
                      listPosition={SelectListPosition.TOP}
                      onButtonClick={() => setPanelIsOpen(true)}
                      list={autocompleteList.map((item) => ({
                        ...item,
                        dob: item.dob ? formatDate(item.dob) : '-',
                      }))}
                      onSearch={onSearch}
                      isLoading={autocompleteIsLoading}
                      onApplicationClick={(id: string) => {
                        setAddErrorVisible(false);

                        if (
                          !companyOfficial?.find(
                            (item) => item.subApplication.id === id,
                          )
                        ) {
                          assignCompanyOfficial(id);
                        } else {
                          setAddErrorVisible(true);
                        }

                        if (searchQuery) {
                          setSearchQuery('');
                          getAutocompleteListAction('');
                        }
                      }}
                      clearState={clearState}
                      tabIndex={0}
                    />
                  )}
                  {assignInProgress && (
                    <div className={cn.assignLoaderWrapper}>
                      <IconLoader />{' '}
                      <span>Company Official assign in progress...</span>
                    </div>
                  )}
                </div>
              )}
          </div>
        </StepPartBlock>

        <SidePanel
          type={SidePanelType.Customers}
          closeOnClickOutside
          isOpen={panelIsOpen}
          sidepanelExpandedKey="application-cof-create"
          onClose={() => {
            if (isDirty) {
              setDirtyPopupVisible(true);
            } else {
              setSidepanelError('');
              setSumSubError('');
              setPanelIsOpen(false);
              reset({
                accessRight: AccessRight.VIEWER,
                applicantId: '',
                deliveryChannelId: '',
                dob: '',
                email: '',
                firstName: '',
                isUSResident: false,
                lastName: '',
                nationalityId: '',
                participatesInPolitics: false,
                phone: '',
                signatureType: '',
                applicationSchemeId: '',
              });
            }
          }}
          title="Create company official application"
          errorSidepanel={sidepanelError}
          footer={
            <div style={{ display: 'flex' }}>
              <ButtonPrimary
                text={
                  isLoading || assignInProgress
                    ? 'Loading...'
                    : 'Create application'
                }
                onClick={onSubmit}
                bold
                disabled={isLoading || assignInProgress}
              />
              <ButtonSecondary
                text="Cancel"
                onClick={() => {
                  if (isDirty) {
                    setDirtyPopupVisible(true);
                  } else {
                    setSidepanelError('');
                    setSumSubError('');
                    setPanelIsOpen(false);
                    reset({
                      accessRight: AccessRight.VIEWER,
                      applicantId: '',
                      deliveryChannelId: '',
                      dob: '',
                      email: '',
                      firstName: '',
                      isUSResident: false,
                      lastName: '',
                      nationalityId: '',
                      participatesInPolitics: false,
                      phone: '',
                      signatureType: '',
                      applicationSchemeId: '',
                    });
                  }
                }}
                bold
              />
              {Object.keys(errors)?.length > 0 && (
                <span className={cn.formError}>
                  There are errors in some fields
                </span>
              )}
            </div>
          }
        >
          <CreateForm
            control={control}
            errors={errors}
            sumSubError={sumSubError}
            applicationSchemes={applicationSchemes?.filter(
              (item) => item.type === ApplicationSchemeType.COMPANY_OFFICIAL,
            )}
          />
        </SidePanel>
        <Popup
          title="You have unsaved changes that will be lost"
          description="Are you sure you want to close this form?"
          onCancelClick={() => setDirtyPopupVisible(false)}
          onOkClick={() => {
            setPanelIsOpen(false);
            setDirtyPopupVisible(false);
            setSumSubError('');
            setSidepanelError('');
            reset({
              accessRight: AccessRight.VIEWER,
              applicantId: '',
              deliveryChannelId: '',
              dob: '',
              email: '',
              firstName: '',
              isUSResident: false,
              lastName: '',
              nationalityId: '',
              participatesInPolitics: false,
              phone: '',
              signatureType: '',
              applicationSchemeId: '',
            });
          }}
          cancelButtonText="Cancel"
          okButtonText="Close"
          isVisible={dirtyPopupVisible}
          parent={document.getElementById('dirty-popup') as HTMLElement}
        />
        <div id="dirty-popup" />
      </div>
    </div>
  );
};

export default NewApplicationCorporateInfoStepFive;
